import React, { useState, useEffect, useCallback } from "react";
import {
  ArrowsClockwise, Plus, Phone, X, Link,
  Envelope, DeviceMobile, Buildings, MapPin,
  UserCircle, Target, MagnifyingGlass
} from "@phosphor-icons/react";
import API from "../services/api";
import MiniDialer from "../components/MiniDialer";
import "./Contacts.css";

export default function Contacts({ isLead = false }) {
  const dialerState = window.__ds || {};
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [detailWidth, setDetailWidth] = useState(360);
  const [dragging, setDragging] = useState(false);
  const [callerId, setCallerId] = useState("");
  const [form, setForm] = useState({ first_name:"", last_name:"", phone:"", mobile:"", email:"", company:"", position:"", lead_source:"" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/contacts?type=${isLead ? "lead" : "contact"}&search=${search}`);
      setContacts(data);
    } catch {}
    setLoading(false);
  }, [search, isLead]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get("/twilio/credentials").then(({ data }) => {
      setCallerId(data.caller_id || data.tollfree_number || "");
    }).catch(() => {});
  }, []);

  // Drag to resize
  const startDrag = (e) => {
    e.preventDefault();
    setDragging(true);
    const startX = e.clientX;
    const startW = detailWidth;
    const onMove = (e) => setDetailWidth(Math.max(280, Math.min(600, startW + (startX - e.clientX))));
    const onUp = () => { setDragging(false); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleSync = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      const { data } = await API.post("/contacts/sync");
      setSyncMsg(`Synced ${data.synced} records`);
      load();
    } catch (err) { setSyncMsg(err.response?.data?.error || "Sync failed"); }
    setSyncing(false);
    setTimeout(() => setSyncMsg(""), 4000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await API.post("/contacts", { ...form, is_lead: isLead });
      setShowAdd(false);
      setForm({ first_name:"", last_name:"", phone:"", mobile:"", email:"", company:"", position:"", lead_source:"" });
      load();
    } catch (err) { alert(err.response?.data?.error || "Failed"); }
  };

  const getInitials = (c) => `${c.first_name?.[0] || ""}${c.last_name?.[0] || ""}`.toUpperCase() || "?";
  const getPhone = (c) => c.phone || c.mobile || null;
  const LEAD_SOURCES = ["Web","Phone","Email","Referral","LinkedIn","Cold Call","Event","Partner","Other"];

  return (
    <div style={{display:"flex",height:"100%",overflow:"hidden"}}>
      <MiniDialer dialerState={dialerState} />
      <div className="contacts-page" style={{flex:1,overflow:"hidden"}}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{isLead ? "Leads" : "Contacts"}</h1>
          <p className="page-subtitle">{contacts.length} {isLead ? "leads" : "contacts"}</p>
        </div>
        <div className="header-actions">
          {syncMsg && <span className="sync-msg">{syncMsg}</span>}
          <button className="btn btn-secondary btn-sm" onClick={handleSync} disabled={syncing}>
            <ArrowsClockwise size={14} weight={syncing ? "bold" : "regular"} />
            Sync Zoho
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            <Plus size={14} weight="bold" />
            Add {isLead ? "Lead" : "Contact"}
          </button>
        </div>
      </div>

      <div className="contacts-toolbar">
        <div className="leads-search-wrap">
          <MagnifyingGlass size={16} style={{color:"var(--color-gray)",flexShrink:0}}/>
          <input className="leads-search-input" placeholder={`Search ${isLead ? "leads" : "contacts"}...`} value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
      </div>
      <div className="toolbar-divider"/>

      <div className="contacts-layout" style={{ position:"relative" }}>
        <div className="contacts-list">
          {loading && <div className="spinner-container"><span className="spinner"/></div>}
          {!loading && contacts.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">{isLead ? <Target size={32} /> : <UserCircle size={32} />}</div>
              <p>No {isLead ? "leads" : "contacts"} found.<br/>Add one or sync from Zoho.</p>
            </div>
          )}
          {contacts.map(c => (
            <div key={c.id} className={`contact-item ${selected?.id === c.id ? "active" : ""}`} onClick={() => setSelected(c)}>
              <div className="contact-avatar">{getInitials(c)}</div>
              <div className="contact-info">
                <div className="contact-name">{c.first_name} {c.last_name}</div>
                <div className="contact-meta">{c.company || getPhone(c) || "—"}</div>
              </div>
              <div className="contact-actions">
                {getPhone(c) && (
                  <button className="btn-icon" onClick={e => { e.stopPropagation(); dialerState.dial?.(getPhone(c), callerId); }} title="Call">
                    <Phone size={15} />
                  </button>
                )}
                {c.zoho_id && <span className="badge badge-teal" style={{ fontSize:9 }}>Zoho</span>}
              </div>
            </div>
          ))}
        </div>

        {selected && (
          <>
            {/* Drag handle */}
            <div
              onMouseDown={startDrag}
              style={{ width:4, cursor:"col-resize", background: dragging ? "var(--color-teal)" : "var(--color-surface)", flexShrink:0, transition:"background 0.2s", zIndex:10 }}
            />
            <div className="contact-detail" style={{ width: detailWidth, minWidth: detailWidth, maxWidth: detailWidth }}>
              <div className="detail-header">
                <div className="detail-avatar">{getInitials(selected)}</div>
                <div>
                  <h2>{selected.first_name} {selected.last_name}</h2>
                  <p>{selected.position}{selected.position && selected.company ? " · " : ""}{selected.company}</p>
                </div>
                <button className="btn-icon" onClick={() => setSelected(null)}><X size={16} /></button>
              </div>
              <div className="detail-body">
                <div className="detail-section">
                  <span className="label">Contact Info</span>
                  {selected.phone && <div className="detail-row"><Phone size={14} /><span>{selected.phone}</span></div>}
                  {selected.mobile && <div className="detail-row"><DeviceMobile size={14} /><span>{selected.mobile}</span></div>}
                  {selected.email && <div className="detail-row"><Envelope size={14} /><span>{selected.email}</span></div>}
                  {selected.company && <div className="detail-row"><Buildings size={14} /><span>{selected.company}</span></div>}
                  {selected.lead_source && <div className="detail-row"><MapPin size={14} /><span>{selected.lead_source}</span></div>}
                </div>
                {selected.zoho_id && (
                  <div className="detail-section">
                    <span className="label">Zoho CRM</span>
                    <div className="detail-row"><Link size={14} /><span style={{ fontFamily:"var(--font-mono)", fontSize:11 }}>{selected.zoho_id}</span></div>
                  </div>
                )}
              </div>
              <div className="detail-footer">
                {getPhone(selected) && (
                  <button className="btn btn-primary" style={{ width:"100%" }} onClick={() => dialerState.dial?.(getPhone(selected), callerId)}>
                    <Phone size={15} weight="fill" /> Call {selected.first_name}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add {isLead ? "Lead" : "Contact"}</h3>
              <button className="btn-icon" onClick={() => setShowAdd(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handleAdd} className="modal-body">
              <div className="form-row">
                <div className="form-group"><label className="label">First Name *</label><input required value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="John" /></div>
                <div className="form-group"><label className="label">Last Name *</label><input required value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Smith" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="label">Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 555 000 0000" /></div>
                <div className="form-group"><label className="label">Mobile</label><input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} placeholder="+1 555 000 0001" /></div>
              </div>
              <div className="form-group"><label className="label">Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="john@company.com" /></div>
              <div className="form-row">
                <div className="form-group"><label className="label">Company</label><input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Acme Inc." /></div>
                <div className="form-group"><label className="label">Position</label><input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} placeholder="CEO" /></div>
              </div>
              {isLead && (
                <div className="form-group">
                  <label className="label">Lead Source</label>
                  <select value={form.lead_source} onChange={e => setForm(f => ({ ...f, lead_source: e.target.value }))}>
                    <option value="">Select source</option>
                    {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save {isLead ? "Lead" : "Contact"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
